import React, { useEffect, useState } from 'react';

import type { UUID } from '@aline/common';
import { makeStyles } from 'tss-react/mui';

import { MOVE_TONE } from './suggestion-tones';

const useStyles = makeStyles()({
  svg: {
    height: '100%',
    left: 0,
    overflow: 'visible',
    pointerEvents: 'none',
    position: 'absolute',
    top: 0,
    width: '100%',
    zIndex: 2,
  },
});

/** How far right of the cards the connector runs, unless the caller says otherwise. */
const DEFAULT_OUTSET_PX = 12;
/** The radius of the two corners where the connector turns. */
const CORNER_PX = 8;

interface Line {
  x1: number;
  x2: number;
  y1: number;
  y2: number;
}

/**
 * A dashed arrow from a move's source strip to its ghost destination, drawn
 * over `containerRef` while a move is hovered or is the current stop. Never
 * always-on: several pending moves would overlap arrows. Both ends are found
 * by their `data-move-anchor` attribute, so the overlay knows nothing about
 * which list they live in.
 */
export const MoveArrowOverlay = React.memo(function MoveArrowOverlay({
  activeMoveId,
  containerRef,
  outsetPx = DEFAULT_OUTSET_PX,
}: {
  activeMoveId: UUID | null;
  containerRef: React.RefObject<HTMLElement | null>;
  /** How far right of the anchors the vertical run sits. */
  outsetPx?: number;
}): JSX.Element | null {
  const { classes } = useStyles();
  const [line, setLine] = useState<Line | null>(null);

  /**
   * A passive effect, not a layout effect. The overlay is a child of the
   * container, and React runs a child's layout effects before it attaches the
   * parent's ref, so a layout effect that ran on mount would find the ref
   * still null, draw nothing and set up no observers. That is the case where
   * the panel opens with a move already current, e.g. from the suggestions
   * stepper; nothing would ever re-measure until the active move changed.
   */
  useEffect(() => {
    const container = containerRef.current;
    if (!container || !activeMoveId) {
      setLine(null);
      return;
    }

    function measure(): void {
      if (!container) return;
      const source = container.querySelector(`[data-move-anchor="${activeMoveId}:source"]`);
      const ghost = container.querySelector(`[data-move-anchor="${activeMoveId}:ghost"]`);
      if (!source || !ghost) {
        setLine(null);
        return;
      }
      const origin = container.getBoundingClientRect();
      const from = source.getBoundingClientRect();
      const to = ghost.getBoundingClientRect();
      /**
       * Both ends sit on the cards' right edges, so the arrow leaves and
       * re-enters at the edge rather than from inside the card.
       */
      setLine({
        x1: from.right - origin.left + container.scrollLeft,
        x2: to.right - origin.left + container.scrollLeft,
        y1: from.top - origin.top + from.height / 2 + container.scrollTop,
        y2: to.top - origin.top + to.height / 2 + container.scrollTop,
      });
    }

    measure();
    const resizeObserver = new ResizeObserver(measure);
    resizeObserver.observe(container);
    /**
     * Rows can shift without the container changing size, e.g. when another
     * suggestion resolves; re-measure so the ends stay on their anchors.
     */
    const mutationObserver = new MutationObserver(measure);
    mutationObserver.observe(container, { childList: true, subtree: true });
    container.addEventListener('scroll', measure);
    return () => {
      resizeObserver.disconnect();
      mutationObserver.disconnect();
      container.removeEventListener('scroll', measure);
    };
  }, [activeMoveId, containerRef]);

  if (!line) return null;

  return (
    <svg aria-hidden className={classes.svg}>
      <defs>
        <marker
          id="playbook-move-arrowhead"
          markerHeight="6"
          markerWidth="6"
          orient="auto"
          refX="5"
          refY="3"
        >
          <path d="M0,0 L6,3 L0,6 z" fill={MOVE_TONE.color} />
        </marker>
      </defs>
      <path
        d={getArrowPath(line, outsetPx)}
        fill="none"
        markerEnd="url(#playbook-move-arrowhead)"
        stroke={MOVE_TONE.color}
        strokeDasharray="4 3"
        strokeWidth={1.5}
      />
    </svg>
  );
});

/**
 * Leaves the source to the right, runs down (or up) beside the cards and comes
 * back in at the ghost, so the path never crosses the text of the rows between
 * them. The two corners are rounded; the radius shrinks when the ends are too
 * close together for a full one.
 */
function getArrowPath({ x1, x2, y1, y2 }: Line, outsetPx: number): string {
  const railX = Math.max(x1, x2) + outsetPx;
  const direction = y2 >= y1 ? 1 : -1;
  const radius = Math.min(CORNER_PX, Math.abs(y2 - y1) / 2);

  return [
    `M ${x1} ${y1}`,
    `H ${railX - radius}`,
    `Q ${railX} ${y1} ${railX} ${y1 + direction * radius}`,
    `V ${y2 - direction * radius}`,
    `Q ${railX} ${y2} ${railX - radius} ${y2}`,
    `H ${x2}`,
  ].join(' ');
}
